import React, { useCallback, useState } from 'react';

import { Popup } from '@progress/kendo-react-popup';
import styled from 'styled-components';

import { Button, ButtonVariants, ComponentSizes, DatePicker } from 'core/ui';
import type { DateRangeFilterType, DateRangeFilterValue } from 'core/ui/DateRangeFilter';
import { safeToDate } from 'core/utils';

interface DateRangeFilterButtonProps {
  title: string;
  value?: DateRangeFilterValue;
  onChange: (value: DateRangeFilterValue) => void;
  disabled?: boolean;
  size?: ComponentSizes;
}

export const DateRangeFilterButton: React.FC<DateRangeFilterButtonProps> = ({ title, value, onChange, disabled = false, size = ComponentSizes.SMALL }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonElement, setButtonElement] = useState<HTMLElement | null>(null);
  const [inputValue, setInputValue] = useState('');

  // Safely convert date values to ensure they're proper Date objects
  const safeValue = React.useMemo(() => {
    if (!value) {
      return undefined;
    }

    if (value.type === 'custom') {
      // Ensure custom dates are proper Date objects
      const safeFromDate = value.fromDate ? safeToDate(value.fromDate) : null;
      const safeToDate_val = value.toDate ? safeToDate(value.toDate) : null;

      return {
        ...value,
        fromDate: safeFromDate,
        toDate: safeToDate_val,
      };
    }

    return value;
  }, [value]);

  // Update input value when safeValue changes
  React.useEffect(() => {
    if (!safeValue || (!safeValue.fromDate && !safeValue.toDate && safeValue.type === 'custom')) {
      setInputValue('');
    } else {
      setInputValue(getDisplayText());
    }
  }, [safeValue]);

  const handleButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setButtonElement(e.currentTarget.parentElement);
      setIsOpen(!isOpen);
    },
    [isOpen],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const parseInputDate = useCallback((input: string): Date | null => {
    if (!input.trim()) return null;

    // Try various date formats
    const formats = [
      // ISO format
      /^\d{4}-\d{2}-\d{2}$/,
      // US format
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,
      // Short US format
      /^\d{1,2}\/\d{1,2}\/\d{2}$/,
      // European format
      /^\d{1,2}\.\d{1,2}\.\d{4}$/,
      // Text format like "Jan 15, 2025"
      /^[A-Za-z]{3}\s+\d{1,2},?\s+\d{4}$/,
    ];

    // Clean the input
    const cleanInput = input.trim();

    // Try to parse the date
    const date = new Date(cleanInput);

    // Check if it's a valid date
    if (!isNaN(date.getTime())) {
      return date;
    }

    return null;
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  }, []);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const parsedDate = parseInputDate(inputValue);
        if (parsedDate) {
          // Single date entry
          onChange({
            type: 'custom',
            fromDate: parsedDate,
            toDate: parsedDate,
          });
        }
      } else if (e.key === 'Escape') {
        setInputValue(getDisplayText());
      }
    },
    [inputValue, parseInputDate, onChange],
  );

  const handleInputBlur = useCallback(() => {
    const parsedDate = parseInputDate(inputValue);
    if (parsedDate) {
      // Single date entry
      onChange({
        type: 'custom',
        fromDate: parsedDate,
        toDate: parsedDate,
      });
    } else {
      // Reset to current value if parsing failed
      setInputValue(getDisplayText());
    }
  }, [inputValue, parseInputDate, onChange]);

  const handleSingleDateChange = useCallback(
    (date: Date | null) => {
      // When a single date is selected, use it as both from and to date
      onChange({
        type: 'custom',
        fromDate: date,
        toDate: date,
      });
      setIsOpen(false);
    },
    [onChange],
  );

  const handleFromDateChange = useCallback(
    (date: Date | null) => {
      onChange({
        type: 'custom',
        fromDate: date,
        toDate: safeValue?.toDate ?? null,
      });
    },
    [onChange, safeValue?.toDate],
  );

  const handleToDateChange = useCallback(
    (date: Date | null) => {
      onChange({
        type: 'custom',
        fromDate: safeValue?.fromDate ?? null,
        toDate: date,
      });
    },
    [onChange, safeValue?.fromDate],
  );

  const handleClear = useCallback(() => {
    onChange({
      type: 'custom',
      fromDate: null,
      toDate: null,
    });
    setInputValue('');
    setIsOpen(false);
  }, [onChange]);

  const handleQuickFilter = useCallback(
    (type: string) => {
      const filterValue: DateRangeFilterValue = {
        type: type as DateRangeFilterType,
        fromDate: null,
        toDate: null,
      };
      onChange(filterValue);
      setIsOpen(false);
    },
    [onChange],
  );

  const getDisplayText = (): string => {
    if (!safeValue || (!safeValue.fromDate && !safeValue.toDate && safeValue.type === 'custom')) {
      return '';
    }

    if (safeValue.type === 'custom' && (safeValue.fromDate || safeValue.toDate)) {
      // Check if it's a single date (from and to are the same)
      const isSingleDate = safeValue.fromDate && safeValue.toDate && safeValue.fromDate.getTime() === safeValue.toDate.getTime();

      if (isSingleDate && safeValue.fromDate) {
        // Single date - show just the date
        return safeValue.fromDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }

      // Date range - show from → to
      const fromStr = safeValue.fromDate
        ? safeValue.fromDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })
        : 'Any';
      const toStr = safeValue.toDate
        ? safeValue.toDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })
        : 'Any';
      return `${fromStr} → ${toStr}`;
    }

    if (safeValue.type !== 'custom') {
      const labels: Record<string, string> = {
        today: 'Today',
        yesterday: 'Yesterday',
        'this-week': 'This Week',
        'last-week': 'Last Week',
        'this-month': 'This Month',
        'last-month': 'Last Month',
        'last-7-days': 'Last 7 Days',
        'last-30-days': 'Last 30 Days',
        'last-90-days': 'Last 90 Days',
        'last-365-days': 'Last 365 Days',
        'this-year': 'This Year',
        'last-year': 'Last Year',
      };
      return labels[safeValue.type] || safeValue.type;
    }

    return '';
  };

  const hasFilter = !!(safeValue && (safeValue.fromDate || safeValue.toDate || safeValue.type !== 'custom'));

  // Check if it's a single date selection
  const isSingleDate = safeValue?.fromDate && safeValue?.toDate && safeValue.fromDate.getTime() === safeValue.toDate.getTime();

  return (
    <>
      <InputContainer $hasFilter={hasFilter}>
        <StyledInput
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          placeholder={title}
          disabled={disabled}
          $hasFilter={hasFilter}
        />
        <CalendarButton variant={ButtonVariants.SECONDARY} size={size} onClick={handleButtonClick} disabled={disabled} $hasFilter={hasFilter}>
          📅
        </CalendarButton>
      </InputContainer>

      <StyledPopup anchor={buttonElement} show={isOpen} onClose={handleClose} offset={{ left: 0, top: 5 }}>
        <PopupContent>
          <SectionTitle>Single Date</SectionTitle>
          <SingleDateSection>
            <DatePicker
              value={isSingleDate ? safeValue?.fromDate : null}
              onChange={(e) => handleSingleDateChange(e.value)}
              size={ComponentSizes.SMALL}
              disabled={disabled}
              placeholder="Select date"
            />
          </SingleDateSection>

          <Divider />

          <SectionTitle>Date Range</SectionTitle>
          <DateRangeSection>
            <DatePicker
              value={isSingleDate ? null : safeValue?.fromDate}
              onChange={(e) => handleFromDateChange(e.value)}
              size={ComponentSizes.SMALL}
              disabled={disabled}
              placeholder="From date"
            />
            <DatePicker
              value={isSingleDate ? null : safeValue?.toDate}
              onChange={(e) => handleToDateChange(e.value)}
              size={ComponentSizes.SMALL}
              disabled={disabled}
              placeholder="To date"
            />
          </DateRangeSection>

          <Divider />

          <SectionTitle>Quick Filters</SectionTitle>
          <QuickOptions>
            <QuickButton onClick={() => handleQuickFilter('today')}>Today</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('yesterday')}>Yesterday</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('last-7-days')}>Last 7 Days</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('last-30-days')}>Last 30 Days</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('last-90-days')}>Last 90 Days</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('last-365-days')}>Last 365 Days</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('this-month')}>This Month</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('last-month')}>Last Month</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('this-year')}>This Year</QuickButton>
            <QuickButton onClick={() => handleQuickFilter('last-year')}>Last Year</QuickButton>
          </QuickOptions>

          <ButtonSection>
            <Button variant={ButtonVariants.SECONDARY} onClick={handleClear} size={ComponentSizes.SMALL}>
              Clear
            </Button>
          </ButtonSection>
        </PopupContent>
      </StyledPopup>
    </>
  );
};

DateRangeFilterButton.displayName = 'DateRangeFilterButton';

const InputContainer = styled.div<{ $hasFilter: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme, $hasFilter }) => ($hasFilter ? theme.colors.primary : '#e0e0e0')};
  border-radius: 4px;
  background-color: white;
  overflow: hidden;
  transition: all 0.2s;
  padding: 0;

  > button.k-button {
    padding: 0;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-within {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const StyledInput = styled.input<{ $hasFilter: boolean }>`
  flex: 1;
  border: none;
  outline: none;
  padding: 2px 2px;
  font-weight: 400;
  color: ${({ theme, $hasFilter }) => ($hasFilter ? theme.colors.textPrimary : theme.colors.textSecondary)};
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CalendarButton = styled(Button)<{ $hasFilter: boolean }>`
  min-width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-left: 1px solid ${({ theme }) => '#e0e0e0'};
  border-radius: 0;
  background-color: ${({ theme, $hasFilter }) => ($hasFilter ? '#f8f9fa' : 'white')};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border-left-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
  }
`;

const StyledPopup = styled(Popup)`
  .k-popup {
    border: 1px solid ${({ theme }) => theme.colors.borderBase};
    background-color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    z-index: 10000;
  }
`;

const PopupContent = styled.div`
  padding: 16px;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 4px;
`;

const SingleDateSection = styled.div`
  display: flex;
  width: 100%;
`;

const DateRangeSection = styled.div`
  display: flex;
  gap: 8px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.borderBase};
  margin: 4px 0;
`;

const QuickOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`;

const QuickButton = styled.button`
  padding: 8px 12px;
  font-size: 12px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.borderBase};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;

  button {
    min-width: 80px;
  }
`;
